import { Form } from '@/components/forms';
import { TimeInput } from '@/components/forms/time-input';
import { Flex, Switch, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { FormikProps } from 'formik';
import * as yup from 'yup';
import { UserAlertDay, UserAlertFrequency } from '../../../../../backend/src/types.generated';
import { ReminderDayInput } from './reminder-day-input';
import { ReminderFrequencyInput } from './reminder-frequency-input';

const validationSchema = yup.object({
	frequency: yup.string().when('binReminderEnabled', {
		is: (value: boolean) => value === true,
		then: (schema) => schema.required(),
		otherwise: (schema) => schema.notRequired(),
	}),
	day: yup.string().when('binReminderEnabled', {
		is: (value: boolean) => value === true,
		then: (schema) => schema.required(),
		otherwise: (schema) => schema.notRequired(),
	}),
	time: yup.string().when('binReminderEnabled', {
		is: (value: boolean) => value === true,
		then: (schema) => schema.required(),
		otherwise: (schema) => schema.notRequired(),
	}),
	from: yup.string().when('frequency', {
		is: (value: UserAlertFrequency) => needsFrom(value),
		then: (schema) => schema.required(),
		otherwise: (schema) => schema.notRequired(),
	}),
	binReminderEnabled: yup.boolean().notRequired(),
});

const needsFrom = (frequency: UserAlertFrequency) =>
	[UserAlertFrequency.Monthly, UserAlertFrequency.Fortnightly].includes(frequency);

export type BinReminderFormValues = {
	id: string | undefined;
	binReminderEnabled: boolean;
	frequency: UserAlertFrequency;
	day: UserAlertDay;
	time: string;
	from: Date | null;
};

type BinReminderFormProps = {
	onSubmit: (values: BinReminderFormValues) => void;
	formRef: React.RefObject<FormikProps<BinReminderFormValues>>;
	initialValues: BinReminderFormValues;
};

export const BinReminderForm = ({ formRef, onSubmit, initialValues }: BinReminderFormProps) => {
	return (
		<Form
			initialValues={initialValues}
			onSubmit={onSubmit}
			innerRef={formRef}
			validationSchema={validationSchema}
		>
			{(formikProps) => {
				const { values, setFieldValue, touched, errors } = formikProps;

				return (
					<>
						<Text fz="24px" fw={900}>
							Bin Pick Up Reminder
						</Text>
						<Flex direction="column" gap="md" w="100%">
							<Switch
								labelPosition="left"
								color="green.1"
								label="Enable Bin Pick Up Reminder"
								name="binReminderEnabled"
								onChange={(value) => setFieldValue('binReminderEnabled', value.target.checked)}
								checked={values.binReminderEnabled}
								error={touched.binReminderEnabled && errors.binReminderEnabled}
							/>
							<ReminderFrequencyInput
								label="Frequency"
								value={values.frequency}
								onChange={(e) => setFieldValue('frequency', e)}
								error={touched.frequency && errors.frequency}
							/>
							<ReminderDayInput
								label="Day"
								value={values.day}
								onChange={(e) => setFieldValue('day', e)}
								error={touched.day && errors.day}
							/>
							{needsFrom(values.frequency) && (
								<DatePickerInput
									label="From"
									value={values.from}
									onChange={(e) => setFieldValue('from', e)}
									error={touched.from && errors.from}
								/>
							)}
							<TimeInput
								value={values.time}
								onChange={(e) => setFieldValue('time', e.currentTarget.value)}
								error={touched.time && errors.time}
							/>
						</Flex>
					</>
				);
			}}
		</Form>
	);
};
